/**
 * Recupera le opzioni di lookup in modo sicuro per un campo specifico.
 * @param {Object} lookupOptions - L'oggetto { fieldName: [{ value, label, ... }], ... }.
 * @param {string} field - Il nome del campo per cui recuperare le opzioni.
 * @param {Array} defaultOptions - Opzioni di default da restituire se non trovate.
 * @returns {Array} L'array di opzioni [{ value, label, ... }] per il campo.
 */
export const getSafeLookupOptions = (lookupOptions, field, defaultOptions = []) => {
  if (!lookupOptions || typeof lookupOptions !== 'object') return defaultOptions;
  if (!field) return defaultOptions;

  const options = lookupOptions[field];

  // Verifica che le opzioni siano un array
  if (!Array.isArray(options)) {
    // Potrebbe essere un log utile in sviluppo
    // console.warn(`getSafeLookupOptions: Opzioni per il campo '${field}' non sono un array.`);
    return defaultOptions;
  }

  return options;
};

/**
 * Normalizza un oggetto di opzioni di lookup per garantire una struttura coerente
 * { value, label, original? } per ogni opzione.
 * @param {Object} lookupOptions - L'oggetto { fieldName: [opzione1, opzione2], ... }.
 * @param {Array<string>} requiredLookupFields - Array opzionale di nomi di campo che devono esistere nell'oggetto normalizzato (anche se vuoti).
 * @returns {Object} L'oggetto lookupOptions normalizzato.
 */
export const normalizeLookupOptions = (lookupOptions, requiredLookupFields = []) => {
  if (!lookupOptions || typeof lookupOptions !== 'object') {
    // console.warn('normalizeLookupOptions: lookupOptions non è un oggetto valido:', lookupOptions);
    return {};
  }

  const normalized = {};

  // Inizializza i campi richiesti come array vuoti se non presenti
  if (Array.isArray(requiredLookupFields)) {
    requiredLookupFields.forEach(field => {
      if (!normalized[field]) { // Evita sovrascrittura se già presente
         normalized[field] = [];
      }
    });
  }

  // Itera sulle opzioni esistenti e normalizzale
  Object.entries(lookupOptions).forEach(([field, options]) => {
    if (!Array.isArray(options)) {
      // console.warn(`normalizeLookupOptions: Le opzioni per '${field}' non sono un array:`, options);
      // Assicura che il campo esista come array vuoto se era richiesto o già presente
      normalized[field] = normalized[field] || [];
      return; // Salta questo campo
    }

    // Normalizza ogni singola opzione nell'array
    normalized[field] = options
      .map((option, idx) => {
        if (!option || typeof option !== 'object') {
          // console.warn(`normalizeLookupOptions: Opzione non valida per '${field}' all'indice ${idx}:`, option);
          // Restituisce un'opzione placeholder o null/undefined per filtrarla dopo
          return null; // O { value: '', label: '(Opzione non valida)' };
        }

        // Struttura attesa dall'hook useLookupOptions: { value, label, original }
        const value = option.value !== undefined && option.value !== null ? option.value : '';
        const label = option.label !== undefined && option.label !== null ? String(option.label) : `(Valore: ${value})`;

        return {
          value: value,
          label: label,
          // Includi l'oggetto originale se presente
          ...(option.original && { original: option.original })
        };
      })
      .filter(Boolean); // Rimuovi eventuali opzioni nulle risultanti da dati non validi
  });

  return normalized;
};


/**
 * Logga informazioni di debug sui lookupOptions in console (solo in sviluppo).
 * @param {string} componentName - Nome del componente che chiama la funzione (per contesto).
 * @param {Object} lookupOptions - L'oggetto lookupOptions da ispezionare.
 */
export const debugLookupOptions = (componentName, lookupOptions) => {
  // Debug logging removed for cleaner console output
};